/** START MY DAY — sequential guided walk through today's actionable work. */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sun, ArrowRight, ArrowLeft, ExternalLink, CheckCircle2, SkipForward, PartyPopper } from "lucide-react";
import { StatusPill, fmtValue } from "@/components/os/OsPrimitives";

const firstName = (u) => (u?.name || u?.email || "there").split(/[\s@]/)[0];

export default function StartMyDay({ open, onOpenChange, brief, user, onAction, onComplete }) {
  const [stage, setStage] = useState("brief"); // brief | walk | done
  const [idx, setIdx] = useState(0);
  const [handled, setHandled] = useState(0);
  const items = brief?.items || [];

  useEffect(() => { if (open) { setStage("brief"); setIdx(0); setHandled(0); } }, [open]);

  const next = () => { if (idx + 1 >= items.length) setStage("done"); else setIdx(idx + 1); };
  const it = items[idx];
  const pct = items.length ? Math.round(((stage === "done" ? items.length : idx) / items.length) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-[color:var(--st-border)]" data-testid="start-my-day" aria-describedby="smd-desc">
        <DialogTitle className="sr-only">Start my day — morning brief</DialogTitle>
        <DialogDescription id="smd-desc" className="sr-only">Guided walk through today's actionable work items.</DialogDescription>
        <div className="bg-[color:var(--st-sidebar)] text-white px-6 py-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">
            <Sun className="h-3.5 w-3.5 text-amber-300" /> Morning brief
          </div>
          {stage === "brief" && <>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2">Good morning, {firstName(user)}.</h2>
            <p className="text-sm text-white/75 mt-1" data-testid="smd-total">You have <span className="font-bold text-white">{brief?.total || 0}</span> item{brief?.total === 1 ? "" : "s"} requiring attention.</p>
          </>}
          {stage === "walk" && <>
            <div className="flex items-center justify-between mt-2">
              <h2 className="font-display text-lg font-bold tracking-tight">Item {idx + 1} of {items.length}</h2>
              <span className="text-[11px] text-white/60">{handled} handled</span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[color:var(--st-accent-bright)] os-progress" style={{ width: `${pct}%` }} /></div>
          </>}
          {stage === "done" && <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2 flex items-center gap-2"><PartyPopper className="h-6 w-6 text-amber-300" /> Day started.</h2>}
        </div>

        <div className="p-6">
          {stage === "brief" && (
            <>
              {items.length === 0 ? (
                <div className="text-center py-6" data-testid="smd-empty">
                  <CheckCircle2 className="h-9 w-9 mx-auto text-[color:var(--st-success)]" />
                  <div className="font-semibold mt-2">You're all caught up.</div>
                  <div className="text-xs text-[color:var(--st-text-muted)] mt-1">Nothing requires your attention right now.</div>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="smd-groups">
                  {(brief?.groups || []).slice(0, 7).map((g) => (
                    <li key={g.label} className="flex items-center justify-between text-sm">
                      <span className="text-[color:var(--st-text)]">{g.label}</span>
                      <span className="font-mono font-semibold text-[color:var(--st-text)] bg-[color:var(--st-surface-alt)] rounded-md px-2 py-0.5 text-xs">{g.count}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex items-center justify-end gap-2">
                <button onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-full text-sm text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]" data-testid="smd-later">Later</button>
                {items.length > 0 && (
                  <button onClick={() => setStage("walk")} data-testid="smd-start"
                    className="h-10 px-5 rounded-full bg-[color:var(--st-accent)] text-white text-sm font-semibold inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.98] transition-[filter,transform]">
                    Start my day <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </>
          )}

          {stage === "walk" && it && (
            <div className="os-fade-in" key={it.key} data-testid="smd-item">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill tone={it.priority === "critical" ? "critical" : it.is_overdue ? "warn" : "info"}>{it.is_overdue ? `${it.overdue_days}d overdue` : it.priority}</StatusPill>
                <span className="text-[11px] text-[color:var(--st-text-subtle)]">{it.category}</span>
              </div>
              <h3 className="font-display font-bold text-lg mt-2 text-[color:var(--st-text)]" data-testid="smd-item-title">{it.title}</h3>
              <div className="text-sm text-[color:var(--st-text-muted)] mt-1">
                {it.customer && <span className="font-medium text-[color:var(--st-text)]">{it.customer}</span>}
                {it.record && <span className="font-mono ml-2 text-xs">{it.record.length > 24 ? it.record.slice(0, 8) : it.record}</span>}
                {it.value_inr != null && <span className="ml-2 font-mono text-xs">{fmtValue(it.value_inr, "inr")}</span>}
              </div>
              {it.next_action && (
                <div className="mt-4 rounded-lg border border-[color:var(--st-accent)]/30 bg-[color:var(--st-accent-soft)] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-accent)] font-semibold">Next action</div>
                  <div className="text-sm font-medium text-[color:var(--st-text)] mt-0.5" data-testid="smd-next-action">{it.next_action}</div>
                </div>
              )}
              <div className="mt-6 flex items-center justify-between gap-2">
                <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="smd-prev"
                  className="h-9 px-3 rounded-full text-xs text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)] disabled:opacity-40 inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
                <div className="flex items-center gap-2">
                  <button onClick={next} data-testid="smd-skip" className="h-9 px-3 rounded-full text-xs text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)] inline-flex items-center gap-1"><SkipForward className="h-3.5 w-3.5" /> Skip</button>
                  {it.can_complete !== false && (
                    <button onClick={async () => { await onComplete(it); setHandled((h) => h + 1); next(); }} data-testid="smd-complete"
                      className="h-9 px-3 rounded-full text-xs font-medium border border-[color:var(--st-success)]/40 text-[color:var(--st-success)] hover:bg-[color:var(--st-success-soft)] inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Done</button>
                  )}
                  <button onClick={() => { onOpenChange(false); onAction(it); }} data-testid="smd-open"
                    className="h-9 px-4 rounded-full text-xs font-semibold bg-[color:var(--st-text)] text-white hover:bg-[#17263C] inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Open</button>
                </div>
              </div>
            </div>
          )}

          {stage === "done" && (
            <div className="text-center py-4" data-testid="smd-done">
              <div className="text-sm text-[color:var(--st-text-muted)]">You walked through {items.length} item{items.length === 1 ? "" : "s"} and handled {handled}.</div>
              <button onClick={() => onOpenChange(false)} data-testid="smd-close" className="mt-5 h-10 px-5 rounded-full bg-[color:var(--st-accent)] text-white text-sm font-semibold">Back to Home</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
